import React, { useState } from "react";
import { Card, Row, Col, Button } from "react-bootstrap";
import { ColumnDef } from "@tanstack/react-table";
import CurrencyFormat from "react-currency-format";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronUp,
  faChevronDown,
  faPencil,
  faTrashCan,
} from "@fortawesome/pro-solid-svg-icons";
import DataTable from "@components/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import FormNumberField from "@components/Forms/FormNumberField";
import ControlledDropDown from "@components/Forms/ControlledDropDown";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import {
  PricingRow,
  PricingDetailRow,
  AdjustmentType,
} from "../types/PricingRow";
import type {
  AdjustModalState,
  ServiceCategoryOption,
} from "../ProjectPricingV2Mspa";
import useStyles from "../styles/useStyles";

interface Props {
  rows: PricingRow[];
  currencyUnit: string;
  isLoading: boolean;
  totalTcv: number;
  totalCost: number;
  totalProfit: number;
  totalMargin: number;
  paymentCreditsTotal: number | null;
  customerPaymentsTotal: number | null;
  showMarginModal: boolean;
  onOpenMarginModal: () => void;
  projectMargin: string;
  setProjectMargin: (value: string) => void;
  marginError: string[] | null;
  onApplyMargin: () => void;
  onCancelMargin: () => void;
  serviceCategories: ServiceCategoryOption[];
  adjustModal: AdjustModalState;
  adjustPrice: string;
  setAdjustPrice: (value: string) => void;
  adjustCost: string;
  setAdjustCost: (value: string) => void;
  adjustRepeats: string;
  setAdjustRepeats: (value: string) => void;
  adjustServiceCategory: string;
  setAdjustServiceCategory: (value: string) => void;
  adjustError: string[] | null;
  onOpenAdjustModal: (adjustmentType: AdjustmentType) => void;
  onApplyAdjustment: () => void;
  onCancelAdjust: () => void;
  canManagePricingAdjustments: boolean;
  onDeleteAdjustments: (ids: string[]) => Promise<void>;
  onEditAdjustment: (detail: PricingDetailRow) => void;
}

interface Section {
  id: string;
  title: string;
  dataRows: PricingRow[];
}

interface DataTableRow {
  id: string;
  source: string;
  revenue: number;
  adjustment: number;
  net: number;
  tcv: number;
  cost: number;
  profit: number;
  margin: number | null;
  adjustmentType?: AdjustmentType;
  collapsedData?: any;
}

const groupIntoSections = (rows: PricingRow[]): Section[] => {
  const sections: Section[] = [];
  let current: Section | null = null;

  for (const row of rows) {
    if (row.rowType === "section") {
      current = { id: row.id, title: row.source, dataRows: [] };
      sections.push(current);
    } else if (row.rowType === "data" && current) {
      current.dataRows.push(row);
    }
  }

  return sections;
};

const Show: React.FC<Props> = ({
  rows,
  currencyUnit,
  isLoading,
  totalTcv,
  totalCost,
  totalProfit,
  totalMargin,
  paymentCreditsTotal,
  customerPaymentsTotal,
  showMarginModal,
  onOpenMarginModal,
  projectMargin,
  setProjectMargin,
  marginError,
  onApplyMargin,
  onCancelMargin,
  serviceCategories,
  adjustModal,
  adjustPrice,
  setAdjustPrice,
  adjustCost,
  setAdjustCost,
  adjustRepeats,
  setAdjustRepeats,
  adjustServiceCategory,
  setAdjustServiceCategory,
  adjustError,
  onOpenAdjustModal,
  onApplyAdjustment,
  onCancelAdjust,
  canManagePricingAdjustments,
  onDeleteAdjustments,
  onEditAdjustment,
}) => {
  const styles = useStyles();
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});
  const [selectedAdjustmentIds, setSelectedAdjustmentIds] = useState<string[]>(
    []
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteQueue, setDeleteQueue] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [id]: !(prev[id] ?? true),
    }));
  };

  const isSectionExpanded = (id: string) => expandedSections[id] ?? true;

  const handleDelete = () => {
    setDeleteQueue([...selectedAdjustmentIds]);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    setIsDeleting(true);
    onDeleteAdjustments(deleteQueue)
      .then(() => {
        setIsDeleting(false);
        setShowDeleteModal(false);
        setSelectedAdjustmentIds([]);
        setDeleteQueue([]);
      })
      .catch(() => {
        setIsDeleting(false);
      });
  };

  const formatCurrency = (value: number) => (
    <CurrencyFormat
      displayType="text"
      prefix={currencyUnit}
      isNumericString={true}
      thousandSeparator={true}
      value={value.toFixed(2)}
    />
  );

  const sections = groupIntoSections(rows);

  const detailSourceCell = (props: any) => (
    <span>{props.row.original.source}</span>
  );

  const editableDetailSourceCell = (props: any) => {
    const detail = props.row.original as PricingDetailRow;
    return (
      <span
        onClick={() => onEditAdjustment(detail)}
        style={{
          color: "#418172",
          textDecoration: "underline",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        {detail.source}
        <FontAwesomeIcon icon={faPencil} style={{ fontSize: "0.85em" }} />
      </span>
    );
  };

  const buildDetailColumns = (
    isAdjustment: boolean
  ): ColumnDef<PricingDetailRow>[] => [
    {
      id: "source",
      header: "Source",
      cell:
        isAdjustment && canManagePricingAdjustments
          ? editableDetailSourceCell
          : detailSourceCell,
    },
    {
      id: "revenue",
      header: "Revenue",
      cell: (props) => (
        <span>{formatCurrency(props.row.original.revenue)}</span>
      ),
    },
    {
      id: "adjustment",
      header: "Adjustment",
      cell: () => <span>---</span>,
    },
    {
      id: "net",
      header: "Net",
      cell: (props) => <span>{formatCurrency(props.row.original.tcv)}</span>,
    },
    {
      id: "tcv",
      header: "TCV",
      cell: (props) => <span>{formatCurrency(props.row.original.tcv)}</span>,
    },
    {
      id: "cost",
      header: "Cost",
      cell: (props) => <span>{formatCurrency(props.row.original.cost)}</span>,
    },
    {
      id: "profit",
      header: "Profit",
      cell: (props) => <span>{formatCurrency(props.row.original.profit)}</span>,
    },
    {
      id: "margin",
      header: "Margin",
      cell: (props) => {
        const margin = props.row.original.margin;
        if (margin === null || isNaN(margin)) return <span>---</span>;
        return <span>{margin.toFixed(2)}%</span>;
      },
    },
  ];

  const dataColumns: ColumnDef<DataTableRow>[] = [
    {
      id: "source",
      header: "Source",
      cell: (props) => {
        const row = props.row.original;
        return (
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            {row.source}
            {row.adjustmentType && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenAdjustModal(row.adjustmentType!);
                }}
                style={{
                  color: "#418172",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: "inherit",
                  fontWeight: "inherit",
                  marginLeft: 10,
                }}
              >
                Add Adjustment
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: "revenue",
      header: "Revenue",
      cell: (props) => (
        <span>{formatCurrency(props.row.original.revenue)}</span>
      ),
    },
    {
      id: "adjustment",
      header: "Adjustment",
      cell: (props) => (
        <span>{formatCurrency(props.row.original.adjustment)}</span>
      ),
    },
    {
      id: "net",
      header: "Net",
      cell: (props) => <span>{formatCurrency(props.row.original.net)}</span>,
    },
    {
      id: "tcv",
      header: "TCV",
      cell: (props) => <span>{formatCurrency(props.row.original.tcv)}</span>,
    },
    {
      id: "cost",
      header: "Cost",
      cell: (props) => <span>{formatCurrency(props.row.original.cost)}</span>,
    },
    {
      id: "profit",
      header: "Profit",
      cell: (props) => <span>{formatCurrency(props.row.original.profit)}</span>,
    },
    {
      id: "margin",
      header: "Margin",
      cell: (props) => {
        const margin = props.row.original.margin;
        if (margin === null || isNaN(margin)) return <span>---</span>;
        return <span>{margin.toFixed(2)}%</span>;
      },
    },
  ];

  const buildTableRows = (dataRows: PricingRow[]): DataTableRow[] =>
    dataRows.map((row) => {
      const hasDetails = (row.detailRows?.length ?? 0) > 0;
      const isAdjustmentRow = !!row.adjustmentType;
      return {
        id: row.id,
        source: row.source,
        revenue: row.revenue,
        adjustment: row.adjustment,
        net: row.net,
        tcv: row.tcv,
        cost: row.cost,
        profit: row.profit,
        margin: row.margin,
        adjustmentType: row.adjustmentType,
        ...(hasDetails
          ? {
              collapsedData: (
                <DataTable
                  data={row.detailRows!}
                  columns={buildDetailColumns(isAdjustmentRow)}
                  selectable={isAdjustmentRow && canManagePricingAdjustments}
                  selectedIds={selectedAdjustmentIds}
                  setSelectedIds={setSelectedAdjustmentIds}
                  classForTable={`${styles.detailTable} ${styles.hideDetailHead}`}
                  striped
                />
              ),
            }
          : {}),
      };
    });

  const renderSection = (section: Section) => {
    const sectionExpanded = isSectionExpanded(section.id);
    const hasExpandableRows = section.dataRows.some(
      (r) => (r.detailRows?.length ?? 0) > 0
    );
    const tableRows = buildTableRows(section.dataRows);

    return (
      <div key={section.id} className={styles.section}>
        <div
          className={styles.sectionHeader}
          onClick={() => toggleSection(section.id)}
        >
          <span className={styles.sectionTitle}>{section.title}</span>
          <FontAwesomeIcon
            icon={sectionExpanded ? faChevronUp : faChevronDown}
            className={styles.sectionChevron}
          />
        </div>
        {sectionExpanded && (
          <div className={styles.sectionBody}>
            <DataTable
              data={tableRows}
              columns={dataColumns}
              selectable={false}
              isCollapsible={hasExpandableRows}
              classForTable={styles.sectionTable}
              striped
            />
          </div>
        )}
      </div>
    );
  };

  const textRight = { textAlign: "right" as const };

  return (
    <div className={`${styles.page} ${styles.columnWidths}`}>
      {handleAlert(
        !!marginError,
        marginError || [],
        () => {},
        "warning",
        ToastAlert
      )}
      <Card className={styles.card}>
        <Card.Header className={styles.cardHeader}>
          <Row className={styles.headerRow}>
            <Col className={styles.title}>Overall Pricing</Col>
            <Col
              className="text-end"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Button className="btnSeawhite" onClick={onOpenMarginModal}>
                Adjust Project Margin
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <hr className={styles.hr} />
        <Card.Body>
          {isLoading ? (
            <ScopeStackSpinner />
          ) : (
            <>
              {selectedAdjustmentIds.length > 0 && (
                <Row className="mb-2">
                  <Col sm={3}>
                    <Button className="deleteBox" onClick={handleDelete}>
                      {selectedAdjustmentIds.length} Selected{" "}
                      <span className="selectedSpan"></span>
                      <FontAwesomeIcon
                        style={{ color: "black" }}
                        icon={faTrashCan}
                      />
                    </Button>
                  </Col>
                </Row>
              )}
              {sections.map(renderSection)}
              {paymentCreditsTotal !== null && (
                <>
                  <Row className={styles.totalRow}>
                    <Col></Col>
                    <Col style={textRight} xs={4}>
                      Payment Credits
                    </Col>
                    <Col style={textRight}>
                      {formatCurrency(paymentCreditsTotal)}
                    </Col>
                    <Col></Col>
                    <Col></Col>
                    <Col></Col>
                  </Row>
                  <Row className={styles.totalRow}>
                    <Col></Col>
                    <Col style={textRight} xs={4}>
                      Customer Payments
                    </Col>
                    <Col style={textRight}>
                      {formatCurrency(customerPaymentsTotal!)}
                    </Col>
                    <Col></Col>
                    <Col></Col>
                    <Col></Col>
                  </Row>
                </>
              )}
            </>
          )}
        </Card.Body>
      </Card>
      <ScopeStackModal
        modalTitle="Edit Project Margin"
        modalBody={
          <div>
            <FormNumberField
              label="New Project Margin (%)"
              placeholder="Enter the percent you want the new project margin to be"
              step="0.01"
              value={projectMargin}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setProjectMargin(e.target.value)
              }
              min={undefined}
              max={undefined}
              required={false}
              defaultValue={undefined}
              readOnly={false}
              fieldFooter={undefined}
            />
          </div>
        }
        button1Text="Cancel"
        handleButton1Click={onCancelMargin}
        button2Text="Apply Margin"
        handleButton2Click={onApplyMargin}
        show={showMarginModal}
        handleClose={onCancelMargin}
      />
      {handleAlert(
        !!adjustError,
        adjustError || [],
        () => {},
        "warning",
        ToastAlert
      )}
      <ScopeStackModal
        modalTitle="Delete Pricing Adjustment"
        modalBody={
          <p>
            Are you sure you want to delete{" "}
            {deleteQueue.length === 1
              ? "this pricing adjustment"
              : `these ${deleteQueue.length} pricing adjustments`}
            ? This cannot be undone.
          </p>
        }
        handleButton2Click={isDeleting ? () => null : confirmDelete}
        handleClose={() => {
          setShowDeleteModal(false);
          setDeleteQueue([]);
        }}
        show={showDeleteModal}
        handleButton1Click={() => {
          setShowDeleteModal(false);
          setDeleteQueue([]);
        }}
        deleteModal={true}
        button1Text="Cancel"
        button2Text="Delete"
      />
      <ScopeStackModal
        modalTitle={
          adjustModal.isRecurring
            ? "Adjust Recurring Cost / Revenue"
            : "Adjust One Time Cost / Revenue"
        }
        modalBody={
          <div>
            <FormNumberField
              label="* Price"
              value={adjustPrice}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setAdjustPrice(e.target.value)
              }
              step="0.01"
              placeholder={undefined}
              min={undefined}
              max={undefined}
              required={true}
              defaultValue={undefined}
              readOnly={false}
              fieldFooter={undefined}
            />
            <FormNumberField
              label="* Cost"
              value={adjustCost}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setAdjustCost(e.target.value)
              }
              step="0.01"
              placeholder={undefined}
              min={undefined}
              max={undefined}
              required={true}
              defaultValue={undefined}
              readOnly={false}
              fieldFooter={undefined}
            />
            {adjustModal.isRecurring && (
              <ControlledDropDown
                label="* Repeats"
                value={adjustRepeats}
                options={[
                  <option key="select" value="select">
                    Select An Option
                  </option>,
                  <option key="monthly" value="monthly">
                    Monthly
                  </option>,
                  <option key="quarterly" value="quarterly">
                    Quarterly
                  </option>,
                  <option key="yearly" value="yearly">
                    Yearly
                  </option>,
                ]}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setAdjustRepeats(e.target.value)
                }
                defaultValue={undefined}
                required={false}
                readOnly={false}
                disabled={false}
                fieldFooter={null}
              />
            )}
            <ControlledDropDown
              label="Service Category (Optional)"
              value={adjustServiceCategory}
              options={[
                <option key="select" value="select">
                  Assign to a service category from the project
                </option>,
                ...serviceCategories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                )),
              ]}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setAdjustServiceCategory(e.target.value)
              }
              defaultValue={undefined}
              required={false}
              readOnly={false}
              disabled={false}
              fieldFooter={null}
            />
          </div>
        }
        button1Text="Cancel"
        handleButton1Click={onCancelAdjust}
        button2Text="Apply Adjustment"
        handleButton2Click={onApplyAdjustment}
        show={adjustModal.show}
        handleClose={onCancelAdjust}
      />
    </div>
  );
};

export default Show;
